import { Component } from '@theme/component';

/**
 * Manages the mobile filter drawer with 2-step navigation.
 * 
 * Step 1: Shows list of filters
 * Step 2: Shows values for selected filter
 * 
 * 
 * @extends {Component}
 */
class FilterDrawerComponent extends Component {
  #isDrawerOpen = false;
  #currentStep = '1';
  #currentFilterId = null;
  #isRestoring = false;

  connectedCallback() {
    super.connectedCallback();
    this.#initDrawerNavigation();
  }

  #initDrawerNavigation() {
    const drawer = this.querySelector('dialog');
    if (!drawer) return;

    const form = drawer.querySelector('.filter-drawer__form');
    if (!form || form._drawerInitialized) return;
    
    form._drawerInitialized = true;

    const filterButtons = drawer.querySelectorAll('[data-drawer-filter]');
    const backButton = drawer.querySelector('[data-drawer-back]');
    const applyButton = drawer.querySelector('[data-drawer-apply]');
    const filterTitle = drawer.querySelector('[data-drawer-filter-title]');

    // Step navigation
    const showStep = (step) => {
      this.#currentStep = step;
      form.dataset.currentStep = step;
    };

    const showFilter = (filterId) => {
      this.#currentFilterId = filterId;
      
      // Hide all filter containers
      const allContainers = drawer.querySelectorAll('[data-drawer-filter-content]');
      allContainers.forEach(c => c.classList.remove('filter-drawer-2-active'));

      // Show selected filter container
      const container = drawer.querySelector(`[data-drawer-filter-content="${filterId}"]`);
      if (container) {
        container.classList.add('filter-drawer-2-active');
        
        // Update title
        const button = drawer.querySelector(`[data-drawer-filter="${filterId}"]`);
        if (button && filterTitle) {
          const titleText = button.querySelector('.text-heading-m');
          filterTitle.textContent = titleText ? titleText.textContent : '';
        }
      }

      showStep('2');
    };
    
    // Restore drawer state after content updates
    const restoreDrawerState = () => {
      if (!this.#isDrawerOpen || this.#isRestoring) return;
      
      this.#isRestoring = true;
      
      try {
        // Keep drawer open
        if (!drawer.hasAttribute('open') && !drawer.open) {
          drawer.setAttribute('open', '');
          drawer.showModal?.();
        }
        
        const form = drawer.querySelector('.filter-drawer__form');
        if (!form) return;
        
        // Restore step only if it's different
        if (this.#currentStep && form.dataset.currentStep !== this.#currentStep) {
          form.dataset.currentStep = this.#currentStep;
        }
        
        // Restore active filter only if needed
        if (this.#currentStep === '2' && this.#currentFilterId) {
          const activeContainer = drawer.querySelector('.filter-drawer__value-container.filter-drawer-2-active');
          const shouldBeActive = drawer.querySelector(`[data-drawer-filter-content="${this.#currentFilterId}"]`);
          
          // Only update if the wrong container is active or none is active
          if (!activeContainer || activeContainer !== shouldBeActive) {
            const allContainers = drawer.querySelectorAll('[data-drawer-filter-content]');
            allContainers.forEach(c => c.classList.remove('filter-drawer-2-active'));
            
            if (shouldBeActive) {
              shouldBeActive.classList.add('filter-drawer-2-active');
              
              const filterTitle = drawer.querySelector('[data-drawer-filter-title]');
              const button = drawer.querySelector(`[data-drawer-filter="${this.#currentFilterId}"]`);
              if (button && filterTitle) {
                const titleText = button.querySelector('.text-heading-m');
                if (titleText && filterTitle.textContent !== titleText.textContent) {
                  filterTitle.textContent = titleText.textContent;
                }
              }
            }
          }
        }
      } finally {
        // Reset the flag after a delay to allow DOM to settle
        setTimeout(() => {
          this.#isRestoring = false;
        }, 100);
      }
    };

    // Filter button clicks
    filterButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const filterId = button.dataset.drawerFilter;
        showFilter(filterId);
      });
    });

    // Back button
    if (backButton) {
      backButton.addEventListener('click', (e) => {
        e.preventDefault();
        showStep('1');
      });
    }

    // Apply button - submit form and close drawer
    if (applyButton) {
      applyButton.addEventListener('click', (e) => {
        e.preventDefault();
        
        // Get the facets-form-component
        const facetsForm = form?.closest('facets-form-component');
        
        // Apply filters
        if (facetsForm && typeof facetsForm.updateFilters === 'function') {
          facetsForm.updateFilters();
        }
        
        // Close drawer immediately
        this.#isDrawerOpen = false;
        const closeButton = drawer.querySelector('[on\\:click*="closeDialog"]');
        if (closeButton) {
          closeButton.click();
        }
      });
    }

    // Clear all button - use event delegation
    drawer.addEventListener('click', (e) => {
      const clearButton = e.target.closest('[data-drawer-clear-all]');
      if (!clearButton) return;
      
      e.preventDefault();
      
      // Get the base collection URL without query params
      const form = drawer.querySelector('.filter-drawer__form');
      if (form) {
        const baseUrl = form.action.split('?')[0];
        window.location.href = baseUrl;
      }
    });

    // Reset to step 1 when drawer opens
    drawer.addEventListener('open', () => {
      this.#isDrawerOpen = true;
      showStep('1');
    });

    // Track when drawer is manually closed
    const closeButton = drawer.querySelector('[on\\:click*="closeDialog"]');
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.#isDrawerOpen = false;
      });
    }

    // Monitor for DOM updates and restore drawer state
    const bodyObserver = new MutationObserver(() => {
      if (this.#isDrawerOpen) {
        requestAnimationFrame(restoreDrawerState);
      }
    });

    bodyObserver.observe(document.body, { childList: true, subtree: true });

    // Also listen for dialog attribute changes
    const drawerObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'open') {
          if (drawer.hasAttribute('open') && !this.#isDrawerOpen) {
            this.#isDrawerOpen = true;
            showStep('1');
          } else if (!drawer.hasAttribute('open') && this.#isDrawerOpen) {
            this.#isDrawerOpen = false;
          }
        }
      });
    });

    drawerObserver.observe(drawer, { attributes: true });
  }
}

if (!customElements.get('filter-drawer-component')) {
  customElements.define('filter-drawer-component', FilterDrawerComponent);
}

